import type { MultiSelectOption } from '@/Components/MultiSelect/MultiSelect';
import ActionStatus from '@/Models/ActionStatus';
import { TextBlockInsertable } from '@/Models/TextBlock/TextBlockModel';
import type TextBlockResult from '@/Models/TextBlock/TextBlockResult';
import type FilingCabinetStore from '@/Stores/FilingCabinetStore';
import type { ColumnSort, PaginationState } from '@tanstack/react-table';
import { t } from 'i18next';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import * as API from '../API';

interface StateData {
    textBlocks: TextBlockResult[];
    pagination: PaginationState;
    sorting: ColumnSort[];
    globalFilter: string;
    procedures: MultiSelectOption[];
    modalities: MultiSelectOption[];
    radiologists: MultiSelectOption[];
    languages: MultiSelectOption[];
    chosenProcedures: MultiSelectOption[];
    chosenModalities: MultiSelectOption[];
    chosenRadiologists: MultiSelectOption[];
    chosenLanguages: MultiSelectOption[];
    showOnlyAutoInsert: boolean;
}

export default class TextBlockOverviewStore {
    state: StateData = {
        globalFilter: '',
        textBlocks: [],
        procedures: [],
        modalities: [],
        radiologists: [],
        languages: [],
        chosenProcedures: [],
        chosenModalities: [],
        chosenLanguages: [],
        chosenRadiologists: [],
        showOnlyAutoInsert: false,
        pagination: { pageIndex: 0, pageSize: 25 },
        sorting: [],
    };

    loading = true;
    isExportingAll = false;
    exportStatus: ActionStatus = ActionStatus.Hidden;

    private readonly fcStore: FilingCabinetStore<StateData>;

    constructor(fcStore: FilingCabinetStore<StateData>) {
        this.fcStore = fcStore;

        makeObservable(this, {
            state: observable,
            loading: observable,
            isExportingAll: observable,
            exportStatus: observable,
            filteredTextBlocks: computed,
            set: action,
            setTextBlocks: action,
            setChosenLanguages: action,
            setChosenRadiologists: action,
            setChosenModalities: action,
            setChosenProcedures: action,
            setState: action,
            setPagination: action,
            setSorting: action,
            reload: action,
            setLoading: action,
            setShowOnlyAutoInsert: action,
            exportAllTextblocksToG2: action,
            closeExportStatusWindow: action,
        });

        this.fcStore.initializeState(this.initOld, this.initNew);

        this.fcStore.updateTitle(t('TextBlocks'));
    }

    private readonly initOld = (state: StateData): void => {
        this.setState({
            globalFilter: state.globalFilter,
            procedures: state.procedures,
            modalities: state.modalities,
            radiologists: state.radiologists,
            textBlocks: state.textBlocks,
            pagination: state.pagination,
            sorting: state.sorting,
            languages: state.languages,
            chosenModalities: state.chosenModalities,
            chosenRadiologists: state.chosenRadiologists,
            chosenProcedures: state.chosenProcedures,
            chosenLanguages: state.chosenLanguages,
            showOnlyAutoInsert: state.showOnlyAutoInsert,
        });
        void API.textblocks.getOverview()
            .then((res) => {
                this.setTextBlocks(res);
                this.setLoading(false);
            })
            .catch((err) => console.error('Could not reload TextBlockOverview', err));
    };

    private readonly initNew = (): void => {
        this.setLoading(true);
        void Promise.all([API.textblocks.getOverview(), API.setup.textBlocks.textBlockOverview()])
            .then(([textBlocks, hydrateData]) => {
                this.setState({
                    globalFilter: '',
                    textBlocks,
                    procedures: hydrateData.Procedures,
                    modalities: hydrateData.Modalities,
                    radiologists: hydrateData.Radiologists.map((x: string) => {
                        return { Value: x, ID: x }; 
                    }),
                    languages: hydrateData.Languages.map((x: string) => {
                        return { Value: x, ID: x }; 
                    }),
                    chosenProcedures: [],
                    chosenModalities: [],
                    chosenLanguages: [],
                    chosenRadiologists: [],
                    pagination: { pageIndex: 0, pageSize: 25 },
                    sorting: [],
                });
                this.setLoading(false);
            });
    };

    reload = (): void => {
        this.setTextBlocks([]);
        this.setLoading(true);
        void API.textblocks.getOverview()
            .then((res) => {
                this.setTextBlocks(res);
                this.setLoading(false);
            })
            .catch((err) => console.error('Could not reload TextBlockOverview', err));
    };

    setState = (newState: any): void => {
        this.state = newState;
        this.fcStore.updateState(this.state);
    };

    set = <K extends keyof StateData>(prop: K, val: any): void => {
        this.state[prop] = val;
        this.fcStore.updateState(this.state);
    };

    setTextBlocks = (val: TextBlockResult[]): void => {
        this.state.textBlocks = val;
        this.fcStore.updateState(this.state);
    };

    setPagination = (val: any): void => {
        this.state.pagination = val;
        this.fcStore.updateState(this.state);
    };

    setSorting = (val: any): void => {
        this.state.sorting = val;
        this.fcStore.updateState(this.state);
    };

    setChosenProcedures = (val: any): void => {
        this.state.chosenProcedures = val;
        this.fcStore.updateState(this.state);
    };

    setChosenModalities = (val: any): void => {
        this.state.chosenModalities = val;
        this.fcStore.updateState(this.state);
    };

    setChosenRadiologists = (val: any): void => {
        this.state.chosenRadiologists = val;
        this.fcStore.updateState(this.state);
    };

    setChosenLanguages = (val: any): void => {
        this.state.chosenLanguages = val;
        this.fcStore.updateState(this.state);
    };

    setShowOnlyAutoInsert = (val: any): void => {
        this.state.showOnlyAutoInsert = val.target.checked;
        this.fcStore.updateState(this.state);
    };

    setLoading = (val: boolean): void => {
        this.loading = val;
    };

    exportAllTextblocksToG2 = async (): Promise<void> => {
        if (this.isExportingAll) {
            return;
        }

        this.isExportingAll = true;
        this.exportStatus = ActionStatus.InProgress;
        try {
            await API.textblocks.exportAllTextblocksToG2();
            runInAction(() => {
                this.exportStatus = ActionStatus.Success;
            });
        }
        catch (err) {
            console.error('Error occured during export', err);
            runInAction(() => {
                this.exportStatus = ActionStatus.Error;
            });
        }
        finally {
            runInAction(() => {
                this.isExportingAll = false;
            });
        }
    };

    closeExportStatusWindow = (): void => {
        this.exportStatus = ActionStatus.Hidden;
        this.isExportingAll = false;
    };

    // NOTE: Changing filters automatically updates the textblock list on the page, but this is not entirely clear sometimes
    //       Adding a fake button to simulate some searching algo being run again with set filters... This will probably be removed when filtering is done on backend
    simulateSearch = (): void => {
        this.setLoading(true);
        setTimeout(() => this.setLoading(false), 150);
    };

    // NOTE: Eventually I expect this to be moved to the backend as well (once we have backend functionality for language filtering as well)
    //       For now, this can stay but it needs to match the backend filtering as much as possible!
    get filteredTextBlocks(): TextBlockResult[] {
        // When all defaults, just return everything
        if (!this.state.globalFilter && this.state.chosenModalities.length === 0 && this.state.chosenProcedures.length === 0 && this.state.chosenLanguages.length === 0 && this.state.chosenRadiologists.length === 0 && !this.state.showOnlyAutoInsert) {
            return this.state.textBlocks;
        }

        const searchTerm = this.state.globalFilter.toLowerCase();
        return this.state.textBlocks.filter((row: TextBlockResult) => {
            if (this.state.showOnlyAutoInsert && !row.AutoInsert) {
                return false;
            }

            const matchRows = ['Name', 'SearchTerm', 'Pronunciation'];
            let matches = false;
            if (searchTerm !== '') {
                matchRows.forEach((r: string) => {
                    if ((row[r as keyof typeof row] as string).toLowerCase().includes(searchTerm)) {
                        matches = true;
                    }
                });
            }
            else {
                matches = true;
            }
            const noProcOrModalityChosen = this.state.chosenProcedures.length === 0 && this.state.chosenModalities.length === 0;
            const procedureMatch = row.ApplicableFor === TextBlockInsertable.SpecificProcedures && (row.Procedures.length === 0 || row.Procedures.some((x) => this.state.chosenProcedures.map((y) => y.ID).includes(x.ID)));
            const modalityMatch = row.ApplicableFor === TextBlockInsertable.SpecificModalities && (row.Modalities.length === 0 || row.Modalities.some((x) => this.state.chosenModalities.map((y) => y.ID).includes(x.ID)));
            const radMatch = this.state.chosenRadiologists.length === 0 || this.state.chosenRadiologists.some((x) => row.ForRadiologists.includes(x.ID as string));
            const langMatch = this.state.chosenLanguages.length === 0 || this.state.chosenLanguages.some((x) => row.Language === x.ID);
            return matches && (row.ApplicableFor === TextBlockInsertable.AllProcedures || procedureMatch || modalityMatch || noProcOrModalityChosen) && radMatch && langMatch;
        });
    };
}
